import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Check, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

const CONTEXT_FIELDS = {
  payment_summary: [
    { key: "courier",        label: "Courier",        type: "text", persist: true, placeholder: "JNE REG / SiCepat / J&T" },
    { key: "shipping_fee",   label: "Shipping fee (IDR)", type: "number", persist: true },
    { key: "payment_method", label: "Payment method", type: "text", persist: true, persistKey: "wa_payment_method", placeholder: "Transfer BCA / GoPay / OVO", default: "BCA" },
    { key: "payment_ref",    label: "Payment reference / VA", type: "text", persist: true, default: "HARI CHRISTANTO - 124 0880 888" },
  ],
  handover_eta: [
    { key: "courier",      label: "Courier", type: "text", persist: true, placeholder: "JNE REG" },
    { key: "handover_eta", label: "Handover ETA",  type: "text", persist: true, placeholder: "Selasa 13 Feb, 15:00 WIB" },
  ],
  tracking_update: [
    { key: "courier",         label: "Courier", type: "text", persist: true },
    { key: "tracking_number", label: "Tracking number", type: "text", persist: true, placeholder: "JX1234567890" },
    { key: "delivery_eta",    label: "Delivery ETA", type: "text", persist: true, placeholder: "Kamis 15 Feb" },
    { key: "tracking_url",    label: "Tracking URL (optional)", type: "text", persist: true, placeholder: "https://..." },
  ],
};

export function OrderStageCard({ stage, order, onLogged, onOrderSaved }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fields = useMemo(() => CONTEXT_FIELDS[stage.key] || [], [stage.key]);
  const history = (order.wa_notifications || []).filter((n) => n.stage === stage.key);
  const last = history[history.length - 1];

  useEffect(() => {
    // Preload persisted values from order if available, otherwise fall back to the field's default.
    const v = {};
    for (const f of fields) {
      const src = f.persistKey || f.key;
      if (order[src] !== undefined && order[src] !== null && order[src] !== "") {
        v[f.key] = order[src];
      } else if (f.default !== undefined) {
        v[f.key] = f.default;
      }
    }
    setValues(v);
  }, [order, stage.key, fields]);

  const buildPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/wa-preview`, {
        stage: stage.key,
        context: values,
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const savePersistedFields = async () => {
    const patch = {};
    for (const f of fields) {
      if (!f.persist) continue;
      const target = f.persistKey || f.key;
      const val = values[f.key];
      if (val === undefined || val === "") continue;
      patch[target] = f.type === "number" ? Number(val) : val;
    }
    if (Object.keys(patch).length === 0) return true;
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/fields`, patch);
      onOrderSaved?.(data);
      return true;
    } catch (err) {
      toast.warning("Message sent, but couldn't save these fields to the order. Please re-enter and retry.");
      return false;
    }
  };

  const openWa = async () => {
    if (!preview) { toast.error("Please preview the message first"); return; }
    // Open synchronously before any await — see RepairStageCard.jsx for why.
    window.open(preview.wa_link, "_blank", "noopener,noreferrer");
    await savePersistedFields();
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/wa-log`, { stage: stage.key });
      onLogged?.(data.entry);
      toast.success(`Sent · ${stage.label_en}`);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openDialog = () => {
    setPreview(null);
    setOpen(true);
    if (fields.length === 0) {
      setTimeout(() => buildPreview(), 0);
    }
  };

  return (
    <>
      <div data-testid={`wa-order-stage-card-${stage.key}`} className={`rounded-2xl border p-4 flex items-start justify-between gap-3 ${last ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">{stage.label_id}</div>
          <div className="font-display font-bold text-zinc-900 text-sm">{stage.label_en}</div>
          {last && (
            <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
              <Check className="w-3 h-3" /> Sent · {new Date(last.sent_at).toLocaleString()}
              {history.length > 1 && <span className="text-zinc-400 ml-1">({history.length}×)</span>}
            </div>
          )}
        </div>
        <Button data-testid={`wa-order-stage-btn-${stage.key}`} onClick={openDialog} size="sm" className={`rounded-full text-xs ${last ? "bg-zinc-100 text-zinc-800 hover:bg-zinc-200" : "bg-brand hover:bg-brand-hover text-white"}`}>
          {last ? "Resend" : "Send"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-brand" /> {stage.label_id} · <span className="text-zinc-500 font-normal text-sm">{stage.label_en}</span>
            </DialogTitle>
          </DialogHeader>

          {fields.length > 0 && (
            <div className="space-y-3 mt-2">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Message details</div>
              {fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-sm font-semibold text-zinc-800">{f.label}</Label>
                  <Input data-testid={`wa-order-field-${f.key}`} type={f.type} value={values[f.key] || ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-10" placeholder={f.placeholder} />
                </div>
              ))}
              <Button data-testid="wa-order-preview-btn" type="button" onClick={buildPreview} disabled={loading} variant="outline" className="rounded-full">
                {loading ? "Building…" : "Preview message"}
              </Button>
            </div>
          )}

          {preview && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Message preview (ID + EN)</div>
              <pre data-testid="wa-order-preview-body" className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 text-xs whitespace-pre-wrap font-mono text-zinc-800 max-h-72 overflow-y-auto">{preview.message}</pre>
              <div className="mt-2 text-xs text-zinc-500">
                Recipient: <span className="font-mono">{preview.phone || "(no phone on file)"}</span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid="wa-order-send-btn" onClick={openWa} className="rounded-full bg-brand hover:bg-brand-hover text-white flex-1">
                  <Send className="w-4 h-4 mr-1.5" /> Open WhatsApp & log
                </Button>
                <a href={preview.wa_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
                  <ExternalLink className="w-3 h-3" /> link only
                </a>
              </div>
            </div>
          )}

          {fields.length === 0 && !preview && (
            <div className="text-sm text-zinc-500 mt-2">Building preview…</div>
          )}

          {history.length > 0 && (
            <div className="mt-6 pt-4 border-t border-zinc-100">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">History</div>
              <ul className="space-y-1 text-xs text-zinc-600">
                {history.slice().reverse().map((h) => (
                  <li key={`${h.stage}-${h.sent_at}`} className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-emerald-600" />
                    {new Date(h.sent_at).toLocaleString()} · by {h.admin_name || h.admin_id}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
